/**
 * Bitwise operations core library.
 *
 * 16 operations total:
 * - 10 binary boolean operations (both A and B are data, compared byte-by-byte)
 * - 2 unary operations (NOT A, NOT B)
 * - 4 shift/rotate operations (A is data, B is the shift amount in bits)
 *
 * XOR is the primary / default operation.
 */

export type OpCategory = "binary" | "unary" | "shift";

export interface BitwiseOperation {
  id: string;
  name: string;
  symbol: string;
  /** Short LaTeX-style description of the truth table or behavior */
  description: string;
  category: OpCategory;
  /** For shift/rotate ops, B is interpreted as a shift amount (integer) */
  bIsShiftAmount?: boolean;
}

/**
 * The 16 bitwise operations.
 *
 * Binary boolean ops follow the standard 2-variable boolean function table.
 * Where the 4 trivial functions (FALSE, TRUE, A, B) would be, we substitute
 * practical shift and rotate operations to fill the 16 slots.
 */
export const OPERATIONS: BitwiseOperation[] = [
  {
    id: "xor",
    name: "XOR",
    symbol: "⊕",
    description: "Exclusive OR — each output bit is 1 when the input bits differ.",
    category: "binary",
  },
  {
    id: "and",
    name: "AND",
    symbol: "∧",
    description: "Bitwise AND — each output bit is 1 only when both input bits are 1.",
    category: "binary",
  },
  {
    id: "or",
    name: "OR",
    symbol: "∨",
    description: "Bitwise OR (inclusive) — each output bit is 1 when at least one input bit is 1.",
    category: "binary",
  },
  {
    id: "nand",
    name: "NAND",
    symbol: "⊼",
    description: "NOT AND — the complement of AND.",
    category: "binary",
  },
  {
    id: "nor",
    name: "NOR",
    symbol: "⊽",
    description: "NOT OR — the complement of OR.",
    category: "binary",
  },
  {
    id: "xnor",
    name: "XNOR",
    symbol: "⊙",
    description: "Exclusive NOR / equivalence — each output bit is 1 when the input bits are the same.",
    category: "binary",
  },
  {
    id: "a-and-not-b",
    name: "A AND NOT B",
    symbol: "A∧¬B",
    description: "Bits of A where B is 0 — masks A with the complement of B.",
    category: "binary",
  },
  {
    id: "not-a-and-b",
    name: "NOT A AND B",
    symbol: "¬A∧B",
    description: "Bits of B where A is 0 — masks B with the complement of A.",
    category: "binary",
  },
  {
    id: "a-implies-b",
    name: "A → B",
    symbol: "A⇒B",
    description: "Material implication — NOT A OR B. Output bit is 0 only when A is 1 and B is 0.",
    category: "binary",
  },
  {
    id: "b-implies-a",
    name: "B → A",
    symbol: "B⇒A",
    description: "Reverse implication — NOT B OR A. Output bit is 0 only when B is 1 and A is 0.",
    category: "binary",
  },
  {
    id: "not-a",
    name: "NOT A",
    symbol: "¬A",
    description: "Bitwise complement of A. B is ignored.",
    category: "unary",
  },
  {
    id: "not-b",
    name: "NOT B",
    symbol: "¬B",
    description: "Bitwise complement of B. A is ignored.",
    category: "unary",
  },
  {
    id: "left-shift",
    name: "Left Shift",
    symbol: "<<",
    description: "Shift A's bits left by N positions. Vacated bits are filled with 0. MSBs are lost.",
    category: "shift",
    bIsShiftAmount: true,
  },
  {
    id: "right-shift",
    name: "Right Shift",
    symbol: ">>",
    description: "Shift A's bits right by N positions (logical). Vacated bits are filled with 0. LSBs are lost.",
    category: "shift",
    bIsShiftAmount: true,
  },
  {
    id: "left-rotate",
    name: "Left Rotate",
    symbol: "ROL",
    description: "Circular left shift of A by N positions. Bits wrapping off the MSB end re-enter at the LSB end.",
    category: "shift",
    bIsShiftAmount: true,
  },
  {
    id: "right-rotate",
    name: "Right Rotate",
    symbol: "ROR",
    description: "Circular right shift of A by N positions. Bits wrapping off the LSB end re-enter at the MSB end.",
    category: "shift",
    bIsShiftAmount: true,
  },
];

export const OPERATION_MAP = Object.fromEntries(
  OPERATIONS.map((op) => [op.id, op]),
) as Record<string, BitwiseOperation>;

// ─── helpers ──────────────────────────────────────────────────────────

/** Pad the shorter array with leading zero bytes so both are the same length. */
function padToSameLength(a: Uint8Array, b: Uint8Array): [Uint8Array, Uint8Array] {
  const maxLen = Math.max(a.length, b.length);
  if (a.length === maxLen && b.length === maxLen) return [a, b];
  const pa = new Uint8Array(maxLen);
  const pb = new Uint8Array(maxLen);
  pa.set(a, maxLen - a.length);
  pb.set(b, maxLen - b.length);
  return [pa, pb];
}

/** Apply a per-byte boolean function to two equal-length byte arrays. */
function binaryOp(
  a: Uint8Array,
  b: Uint8Array,
  fn: (x: number, y: number) => number,
): Uint8Array {
  const [pa, pb] = padToSameLength(a, b);
  const result = new Uint8Array(pa.length);
  for (let i = 0; i < pa.length; i++) {
    result[i] = fn(pa[i], pb[i]) & 0xff;
  }
  return result;
}

/** Bitwise complement of every byte. */
function notBytes(data: Uint8Array): Uint8Array {
  const result = new Uint8Array(data.length);
  for (let i = 0; i < data.length; i++) {
    result[i] = (~data[i]) & 0xff;
  }
  return result;
}

// ─── binary boolean operations ────────────────────────────────────────

const BINARY_FNS: Record<string, (x: number, y: number) => number> = {
  xor: (x, y) => x ^ y,
  and: (x, y) => x & y,
  or: (x, y) => x | y,
  nand: (x, y) => ~(x & y),
  nor: (x, y) => ~(x | y),
  xnor: (x, y) => ~(x ^ y),
  "a-and-not-b": (x, y) => x & ~y,
  "not-a-and-b": (x, y) => ~x & y,
  "a-implies-b": (x, y) => ~x | y,
  "b-implies-a": (x, y) => x | ~y,
};

// ─── shift / rotate operations ────────────────────────────────────────

/** Left shift a big-endian byte array by n bits. Result has the same length. */
function leftShift(data: Uint8Array, n: number): Uint8Array {
  if (n <= 0 || data.length === 0) return data.slice();
  const result = new Uint8Array(data.length);
  const byteShift = Math.floor(n / 8);
  const bitShift = n % 8;

  for (let i = 0; i < data.length; i++) {
    const srcIdx = i + byteShift;
    if (srcIdx < data.length) {
      result[i] = (data[srcIdx] << bitShift) & 0xff;
    }
    if (bitShift > 0 && srcIdx + 1 < data.length) {
      result[i] |= data[srcIdx + 1] >>> (8 - bitShift);
    }
  }
  return result;
}

/** Logical right shift a big-endian byte array by n bits. Same length. */
function rightShift(data: Uint8Array, n: number): Uint8Array {
  if (n <= 0 || data.length === 0) return data.slice();
  const result = new Uint8Array(data.length);
  const byteShift = Math.floor(n / 8);
  const bitShift = n % 8;

  for (let i = data.length - 1; i >= 0; i--) {
    const srcIdx = i - byteShift;
    if (srcIdx >= 0) {
      result[i] = (data[srcIdx] >>> bitShift) & 0xff;
    }
    if (bitShift > 0 && srcIdx - 1 >= 0) {
      result[i] |= (data[srcIdx - 1] << (8 - bitShift)) & 0xff;
    }
  }
  return result;
}

/** Circular left rotate of a big-endian byte array by n bits. */
function rotateLeft(data: Uint8Array, n: number): Uint8Array {
  if (data.length === 0) return data.slice();
  const totalBits = data.length * 8;
  const shift = ((n % totalBits) + totalBits) % totalBits;
  if (shift === 0) return data.slice();

  const ls = leftShift(data, shift);
  const rs = rightShift(data, totalBits - shift);
  const result = new Uint8Array(data.length);
  for (let i = 0; i < data.length; i++) {
    result[i] = ls[i] | rs[i];
  }
  return result;
}

/** Circular right rotate of a big-endian byte array by n bits. */
function rotateRight(data: Uint8Array, n: number): Uint8Array {
  if (data.length === 0) return data.slice();
  const totalBits = data.length * 8;
  const shift = ((n % totalBits) + totalBits) % totalBits;
  if (shift === 0) return data.slice();
  return rotateLeft(data, totalBits - shift);
}

// ─── main dispatch ────────────────────────────────────────────────────

/**
 * Compute a bitwise operation.
 *
 * @param opId  Operation ID (see OPERATIONS)
 * @param a     Input A as a byte array (big-endian)
 * @param b     Input B as a byte array (big-endian) — for shift/rotate ops,
 *              this is interpreted as a shift amount via bytesToShiftAmount
 * @returns     Result as a byte array
 */
export function compute(
  opId: string,
  a: Uint8Array,
  b: Uint8Array,
): Uint8Array {
  const fn = BINARY_FNS[opId];
  if (fn) return binaryOp(a, b, fn);

  switch (opId) {
    case "not-a":
      return notBytes(a);
    case "not-b":
      return notBytes(b);
    case "left-shift":
      return leftShift(a, bytesToShiftAmount(b));
    case "right-shift":
      return rightShift(a, bytesToShiftAmount(b));
    case "left-rotate":
      return rotateLeft(a, bytesToShiftAmount(b));
    case "right-rotate":
      return rotateRight(a, bytesToShiftAmount(b));
    default:
      throw new Error(`Unknown operation: ${opId}`);
  }
}

/** Convert a byte array to a non-negative integer for use as a shift amount. */
export function bytesToShiftAmount(bytes: Uint8Array): number {
  let result = 0;
  for (let i = 0; i < bytes.length && i < 4; i++) {
    result = (result << 8) | bytes[i];
  }
  // Support longer values via BigInt fallback
  if (bytes.length > 4) {
    let big = 0n;
    for (const b of bytes) {
      big = (big << 8n) | BigInt(b);
    }
    return Number(big);
  }
  return result;
}

// ─── bit utilities ────────────────────────────────────────────────────

/** Convert a byte array to a flat array of 0/1 bits (MSB first per byte). */
export function bytesToBits(bytes: Uint8Array): number[] {
  const bits: number[] = [];
  for (const byte of bytes) {
    for (let i = 7; i >= 0; i--) {
      bits.push((byte >> i) & 1);
    }
  }
  return bits;
}

/** Convert a flat array of 0/1 bits (MSB first) back to a byte array. */
export function bitsToBytes(bits: number[]): Uint8Array {
  const byteCount = Math.ceil(bits.length / 8);
  const bytes = new Uint8Array(byteCount);
  for (let i = 0; i < bits.length; i++) {
    if (bits[i]) {
      const byteIdx = Math.floor(i / 8);
      const bitIdx = 7 - (i % 8);
      bytes[byteIdx] |= 1 << bitIdx;
    }
  }
  return bytes;
}
